/**
 * Informational notification row — for types that don't require a creator
 * decision (new_submission, challenge_ending_soon, outjemmed,
 * submission_removed, submission_auto_removed).
 *
 * For the actionable 'submission_flagged' type, see NotificationPanel.
 */

import { Clock, ImageUp, ShieldAlert, ShieldCheck, Trophy, type LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  CONTENT_VIOLATION_LOCKOUT_THRESHOLD,
  type NotificationDetail,
  type NotificationType,
} from '@jemmi/schemas';

import { cn } from '@/lib/cn';

type Props = {
  notification: NotificationDetail;
  onRead: (notificationId: string) => void;
};

const ICON_BY_TYPE: Record<Exclude<NotificationType, 'submission_flagged'>, LucideIcon> = {
  new_submission: ImageUp,
  challenge_ending_soon: Clock,
  outjemmed: Trophy,
  submission_removed: ShieldAlert,
  // Neutral/informational — nothing for the creator to act on, so this
  // deliberately does NOT get the red "violation" treatment below.
  submission_auto_removed: ShieldCheck,
};

export function NotificationItem({ notification, onRead }: Props) {
  const isUnread = !notification.readAt;
  const isViolation = notification.type === 'submission_removed';
  const Icon = ICON_BY_TYPE[notification.type as Exclude<NotificationType, 'submission_flagged'>];

  return (
    <Link
      to={`/challenge/${notification.challengeId}`}
      onClick={() => {
        if (isUnread) onRead(notification.id);
      }}
      className={cn(
        'flex items-start gap-3 rounded-xl border p-4 transition-colors',
        isViolation
          ? 'border-red-200 bg-red-50 hover:bg-red-100'
          : isUnread
            ? 'border-brand-200 bg-brand-50 hover:bg-brand-100'
            : 'border-slate-200 bg-white hover:bg-slate-50',
      )}
    >
      <div
        className={cn(
          'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full',
          isViolation
            ? 'bg-red-100 text-red-700'
            : isUnread
              ? 'bg-brand-100 text-brand-700'
              : 'bg-slate-100 text-slate-500',
        )}
      >
        <Icon className="h-4 w-4" />
      </div>

      <div className="min-w-0 flex-1">
        <p
          className={cn(
            'text-sm',
            isViolation
              ? 'font-semibold text-red-900'
              : isUnread
                ? 'font-semibold text-slate-900'
                : 'text-slate-700',
          )}
        >
          {notification.message}
        </p>

        {isViolation && notification.violationCount !== undefined && (
          <p className="mt-1 text-sm font-medium text-red-700">
            {notification.violationCount >= CONTENT_VIOLATION_LOCKOUT_THRESHOLD
              ? `This was violation ${notification.violationCount} of ${CONTENT_VIOLATION_LOCKOUT_THRESHOLD} — your account has been locked.`
              : `This is violation ${notification.violationCount} of ${CONTENT_VIOLATION_LOCKOUT_THRESHOLD}. Your account will be locked after ${CONTENT_VIOLATION_LOCKOUT_THRESHOLD}.`}
          </p>
        )}

        <p className={cn('mt-1 text-xs', isViolation ? 'text-red-600/70' : 'text-slate-500')}>
          {new Date(notification.createdAt).toLocaleString()}
        </p>
      </div>

      {isUnread && (
        <span
          className={cn(
            'mt-1.5 h-2 w-2 flex-shrink-0 rounded-full',
            isViolation ? 'bg-red-600' : 'bg-brand-600',
          )}
          aria-label="Unread"
        />
      )}
    </Link>
  );
}
